import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const HiddenGemsKampala = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '10 Hidden Gems in Kampala | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "10 Hidden Gems in Kampala You Can't Miss";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  const hiddenGems = [
    {
      name: "Kabaka's Lake",
      description: "The largest man-made lake in Uganda, with rich historical significance.",
      image: "https://imgur.com/EzHnSsj.jpg"
    },
    {
      name: "Gadafi National Mosque",
      description: "Stunning architecture and panoramic views of the city.",
      image: "https://imgur.com/LTxFu0W.jpg"
    },
    {
      name: "Uganda Museum",
      description: "A treasure trove of Uganda's cultural heritage.",
      image: "https://imgur.com/4kwMnNu.jpg"
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/EzHnSsj.jpg"
            alt="Kampala City"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/lCTURxX.jpg"
                alt="Sarah Namukasa"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Sarah Namukasa</h3>
                <p className="text-white/80 text-sm">City Guide</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                City Guide
              </div>
              
              <h1 className="text-4xl font-bold">10 Hidden Gems in Kampala You Can't Miss</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 12, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  6 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Kampala
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Beyond the well-known attractions, Kampala harbors countless hidden treasures waiting 
                  to be discovered. From secret viewpoints to local cultural spots, these lesser-known 
                  gems offer authentic experiences of Uganda's vibrant capital.
                </p>

                {hiddenGems.map((gem, index) => (
                  <div key={index} className="my-8">
                    <img 
                      src={gem.image}
                      alt={gem.name}
                      className="rounded-lg w-full mb-4"
                    />
                    <h2>{gem.name}</h2>
                    <p>{gem.description}</p>
                  </div>
                ))}

                <h2>Local Markets and Shopping</h2>
                <p>
                  Kampala's markets are the heart of local life. While tourists flock to the main 
                  craft markets, there are several hidden spots where you can find unique treasures 
                  and experience authentic local culture.
                </p>

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Local Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Visit markets early in the morning for the best experience</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Learn basic Luganda phrases to connect with locals</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Use bodabodas (motorcycle taxis) for easy navigation</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Try local street food at recommended spots</span>
                    </li>
                  </ul>
                </div>

                <h2>Cultural Experiences</h2>
                <p>
                  Kampala's cultural scene is rich and diverse. From traditional performances to 
                  modern art galleries, there's something for every cultural enthusiast.
                </p>

                <h2>Getting Around</h2>
                <p>
                  Navigating Kampala can be challenging for visitors. Here are some tips for 
                  exploring these hidden gems efficiently and safely.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Kampala", "City Tours", "Local Life", "Culture", "Urban Exploration"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                  >
                    <Instagram className="h-5 w-5 mr-2" />
                    Instagram
                  </button>
                  <button
                    onClick={() => handleShare('native')}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/lCTURxX.jpg"
                      alt="Sarah Namukasa"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Sarah Namukasa</h3>
                      <p className="text-gray-600">City Guide</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Sarah is a Kampala native with extensive knowledge of the city's hidden treasures 
                    and cultural spots.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#markets" className="block text-gray-600 hover:text-ta-green">Local Markets</a>
                    <a href="#cultural" className="block text-gray-600 hover:text-ta-green">Cultural Experiences</a>
                    <a href="#navigation" className="block text-gray-600 hover:text-ta-green">Getting Around</a>
                    <a href="#tips" className="block text-gray-600 hover:text-ta-green">Local Tips</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/kampala-food-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/4kwMnNu.jpg"
                          alt="Kampala Food Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Kampala's Best Street Food Guide
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">8 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/kampala-nightlife" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/EzHnSsj.jpg"
                          alt="Kampala Nightlife"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Exploring Kampala's Vibrant Nightlife
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">6 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default HiddenGemsKampala;